/**
 * Edit a person.
 *
 * WHY THIS EXISTS SEPARATELY FROM THE INLINE CELLS. Those edit the RELATIONSHIP — strength, status,
 * next move, follow-up, owner — one value at a time, which is right for the daily scan down a grid.
 * They never edited the CONTACT, so a mistyped surname or a changed email could be entered once and
 * never corrected. A field you can create and cannot fix is worse than one you never had.
 *
 * ONE SAVE IS ONE REQUEST. Name, email, phone, organization, relationship types and the triad go in
 * a single PATCH, so a form that fails fails as a whole rather than leaving half the edits applied.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { tfApi } from '../../api/tf_api'
import { useApp } from '../../contexts/app_context'
import { ModalShell } from './primitives'
import type { PersonProfile } from '../../types/tf_types'

interface DuplicateInfo {
  contactId: number
  displayName: string
  message: string
}

export function EditPersonModal({ profile, onClose, onSaved }: {
  profile: PersonProfile
  onClose: () => void
  onSaved: () => void
}) {
  const { options, notify, notifyError } = useApp()
  const navigate = useNavigate()
  const contact = profile.Contact
  const relationship = profile.Relationship ?? {}

  const [form, setForm] = useState({
    FirstName: contact.FirstName ?? '',
    LastName: contact.LastName ?? '',
    DisplayName: contact.DisplayName ?? '',
    JobTitle: contact.JobTitle ?? '',
    Department: contact.Department ?? '',
    PrimaryEmail: contact.PrimaryEmail ?? '',
    PrimaryPhone: contact.PrimaryPhone ?? '',
    LinkedInPage: contact.LinkedInPage ?? '',
    WebPage: contact.WebPage ?? '',
    OrganizationName: profile.Organization?.CompanyName ?? '',
    RelationshipStrength: relationship.RelationshipStrength ?? '',
    ContactStatus: relationship.ContactStatus ?? '',
    LeadStatus: relationship.LeadStatus ?? '',
    OwnerUserId: relationship.OwnerUserId ?? '',
    Priority: relationship.Priority ?? '',
    Notes: contact.Notes ?? '',
  })
  const [types, setTypes] = useState<string[]>(
    (profile.RelationshipTypes ?? []).map((t: any) => t.RelationshipType).filter(Boolean),
  )
  const [busy, setBusy] = useState(false)
  const [duplicate, setDuplicate] = useState<DuplicateInfo | null>(null)

  const set = (field: keyof typeof form) => (value: string) => {
    setForm((f) => ({ ...f, [field]: value }))
    if (field === 'PrimaryEmail') setDuplicate(null)
  }

  const toggleType = (value: string) =>
    setTypes((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    )

  const identifiable = Boolean(
    form.FirstName.trim() || form.LastName.trim() || form.DisplayName.trim() || form.PrimaryEmail.trim(),
  )

  const save = async () => {
    setBusy(true)
    setDuplicate(null)
    try {
      //  Everything is sent, including the fields that were cleared — the empty string is a
      //  deliberate "remove this", and dropping it would make a field impossible to blank once set.
      await tfApi.updatePerson(contact.ContactId, { ...form, RelationshipTypes: types })
      notify({ tone: 'ok', title: 'Saved', body: 'Their details are updated.' })
      onSaved()
      onClose()
    } catch (err: any) {
      const detail = err?.detail?.detail ?? err?.detail
      if (err?.status === 409 && detail?.ContactId) {
        setDuplicate({
          contactId: Number(detail.ContactId),
          displayName: String(detail.DisplayName ?? 'That contact'),
          message: String(detail.message ?? 'Somebody else already has that email.'),
        })
      } else {
        notifyError('Could not save these changes', err)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell
      title={`Edit ${contact.DisplayName ?? 'person'}`}
      description="Their own details. Status, next move and follow-up can also be changed here, or in place from the grid."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="button" disabled={busy || !identifiable}
                  onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      {duplicate && (
        <div className="banner warn" style={{ marginBottom: 12 }}>
          <span className="material-symbols-outlined">person_search</span>
          <div>
            <b>{duplicate.message}</b>
            <div style={{ marginTop: 4 }}>
              Two records sharing an email split the history between them, so neither answers
              “when did we last speak”.
            </div>
            <button className="btn secondary sm" type="button" style={{ marginTop: 8 }}
                    onClick={() => { navigate(`/relationships/people/${duplicate.contactId}`); onClose() }}>
              <span className="material-symbols-outlined">open_in_new</span>
              Open {duplicate.displayName}
            </button>
          </div>
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="form-label" htmlFor="ep-first">First name</label>
          <input id="ep-first" className="form-input" value={form.FirstName}
                 onChange={(e) => set('FirstName')(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="ep-last">Last name</label>
          <input id="ep-last" className="form-input" value={form.LastName}
                 onChange={(e) => set('LastName')(e.target.value)} />
        </div>

        <div className="field span-2">
          <label className="form-label" htmlFor="ep-display">Display name</label>
          <input id="ep-display" className="form-input" value={form.DisplayName}
                 onChange={(e) => set('DisplayName')(e.target.value)} />
          <span className="form-hint">
            Leave it matching the first and last name and it follows them; type one and it stays.
          </span>
        </div>

        <div className="field">
          <label className="form-label" htmlFor="ep-email">Email</label>
          <input id="ep-email" className="form-input" type="email" value={form.PrimaryEmail}
                 onChange={(e) => set('PrimaryEmail')(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="ep-phone">Phone</label>
          <input id="ep-phone" className="form-input" value={form.PrimaryPhone}
                 onChange={(e) => set('PrimaryPhone')(e.target.value)} />
        </div>

        <div className="field">
          <label className="form-label" htmlFor="ep-org">Organization</label>
          <input id="ep-org" className="form-input" value={form.OrganizationName}
                 onChange={(e) => set('OrganizationName')(e.target.value)} />
          <span className="form-hint">A name that is not already an account creates one.</span>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="ep-title">Job title</label>
          <input id="ep-title" className="form-input" value={form.JobTitle}
                 onChange={(e) => set('JobTitle')(e.target.value)} />
        </div>

        <div className="field">
          <label className="form-label" htmlFor="ep-dept">Department</label>
          <input id="ep-dept" className="form-input" value={form.Department}
                 onChange={(e) => set('Department')(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="ep-owner">Owner</label>
          <input id="ep-owner" className="form-input" value={form.OwnerUserId}
                 onChange={(e) => set('OwnerUserId')(e.target.value)} />
        </div>

        <div className="field span-2">
          <label className="form-label" htmlFor="ep-linkedin">LinkedIn page</label>
          <input id="ep-linkedin" className="form-input" value={form.LinkedInPage}
                 onChange={(e) => set('LinkedInPage')(e.target.value)} />
        </div>
        <div className="field span-2">
          <label className="form-label" htmlFor="ep-web">Web page</label>
          <input id="ep-web" className="form-input" value={form.WebPage}
                 onChange={(e) => set('WebPage')(e.target.value)} />
        </div>

        <div className="field span-2">
          <span className="form-label">How do you know them?</span>
          <div className="chip-row" style={{ marginTop: 4 }}>
            {options('RELATIONSHIP_TYPE').map((v) => (
              <button key={v.ValueCode} type="button"
                      className={`chip${types.includes(v.ValueLabel) ? ' on' : ''}`}
                      onClick={() => toggleType(v.ValueLabel)}>
                {v.ValueLabel}
              </button>
            ))}
          </div>
          {/* Unticking removes the type. An add-only editor would make a wrongly-applied type
              permanent. */}
          <span className="form-hint">Unticking one removes it.</span>
        </div>

        <div className="field">
          <label className="form-label" htmlFor="ep-strength">Relationship strength</label>
          <select id="ep-strength" className="form-input" value={form.RelationshipStrength}
                  onChange={(e) => set('RelationshipStrength')(e.target.value)}>
            <option value="">— not set —</option>
            {options('RELATIONSHIP_STRENGTH').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="ep-status">Status</label>
          <select id="ep-status" className="form-input" value={form.ContactStatus}
                  onChange={(e) => set('ContactStatus')(e.target.value)}>
            <option value="">— not set —</option>
            {options('CONTACT_STATUS').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="form-label" htmlFor="ep-lead">Lead status</label>
          <select id="ep-lead" className="form-input" value={form.LeadStatus}
                  onChange={(e) => set('LeadStatus')(e.target.value)}>
            <option value="">— not set —</option>
            {options('LEAD_STATUS').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="form-label" htmlFor="ep-priority">Priority</label>
          <select id="ep-priority" className="form-input" value={form.Priority}
                  onChange={(e) => set('Priority')(e.target.value)}>
            <option value="">— not set —</option>
            {options('PRIORITY').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>{v.ValueLabel}</option>
            ))}
          </select>
        </div>

        <div className="field span-2">
          <label className="form-label" htmlFor="ep-notes">Notes</label>
          <textarea id="ep-notes" className="form-input" rows={3} value={form.Notes}
                    onChange={(e) => set('Notes')(e.target.value)} />
        </div>
      </div>

      {!identifiable && (
        <p className="person-sub" style={{ marginTop: 10 }}>
          A name or an email is needed — without one there is nothing here to identify a person by.
        </p>
      )}
    </ModalShell>
  )
}
